import { useState, useEffect } from "react";
import { useUserCatalogCompanies } from "./useCatalog";
import { useAuth } from "@/context/AuthContext";

const THRESHOLDS = [5, 10, 20, 50];

function storageKey(userId: string, type: "responded" | "shown") {
  return `easly_feedback_${type}_${userId}`;
}

function hasResponded(userId: string) {
  return localStorage.getItem(storageKey(userId, "responded")) === "true";
}

function getShownThresholds(userId: string): number[] {
  try {
    const raw = localStorage.getItem(storageKey(userId, "shown"));
    return raw ? (JSON.parse(raw) as number[]) : [];
  } catch {
    return [];
  }
}

function markThresholdShown(userId: string, threshold: number) {
  const shown = getShownThresholds(userId);
  if (!shown.includes(threshold)) {
    localStorage.setItem(storageKey(userId, "shown"), JSON.stringify([...shown, threshold]));
  }
}

function markResponded(userId: string) {
  localStorage.setItem(storageKey(userId, "responded"), "true");
}

export function useFeedbackLoop() {
  const { user } = useAuth();
  const { data: userCompanies } = useUserCatalogCompanies(user?.userId, user?.accessToken);
  const analysisCount = userCompanies?.total ?? 0;

  const [isOpen, setIsOpen] = useState(false);
  const [activeThreshold, setActiveThreshold] = useState<number | null>(null);

  useEffect(() => {
    if (!user?.userId || isOpen) return;
    if (hasResponded(user.userId)) return;

    const shown = getShownThresholds(user.userId);
    const eligible = THRESHOLDS.filter((t) => analysisCount >= t && !shown.includes(t));

    if (eligible.length > 0) {
      setActiveThreshold(eligible[0]);
      setIsOpen(true);
    }
    // isOpen intentionally excluded from deps — we only recheck when count or user changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId, analysisCount]);

  const onDismiss = () => {
    if (!user?.userId || activeThreshold === null) return;
    markThresholdShown(user.userId, activeThreshold);
    setIsOpen(false);
  };

  const onRespond = () => {
    if (!user?.userId) return;
    markResponded(user.userId);
    if (activeThreshold !== null) markThresholdShown(user.userId, activeThreshold);
    setIsOpen(false);
  };

  return { isOpen, onDismiss, onRespond };
}
